import {
  Grid,
  TextField,
  Autocomplete,
  createFilterOptions,
  AutocompleteInputChangeReason,
  Tooltip,
} from '@mui/material'
import { DateSearch } from '../../SearchBar/DateSearch'
import {
  useAllUserOptionsQuery,
  useCustomerAutocompleteOptionsLazyQuery,
  useOriginNumberAutoCompleteOptionsLazyQuery,
} from '@kistill/client/data-access'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import { useCallback, useContext, useEffect, useState } from 'react'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface OriginFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function OriginFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: OriginFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('origin', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset } =
    useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)
  const [inputValueCustomer, setInputValueCustomer] = useState('')
  const [inputValueOrigin, setInputValueOrigin] = useState('')
  const [customerOptions, setCustomerOptions] = useState<SelectItem[]>([])
  const [originOptions, setOriginOptions] = useState<SelectItem[]>([])

  const { data: userOptionData } = useAllUserOptionsQuery()
  const userOptions = userOptionData?.allUserOptions as SelectItem[]

  const [getOriginNumberOptions] = useOriginNumberAutoCompleteOptionsLazyQuery({
    variables: { query: inputValueOrigin },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.originNumberOptions) {
        setOriginOptions(
          data.originNumberOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const [getCustomerOptions] = useCustomerAutocompleteOptionsLazyQuery({
    variables: { query: inputValueCustomer },
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      if (data.customerOptions) {
        setCustomerOptions(
          data.customerOptions.map((x) => {
            return { id: x?.id, title: x.title }
          }),
        )
      }
    },
  })

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'origin',
    onEmptySync: () => {
      setInputValueCustomer('')
      setInputValueOrigin('')
    },
    onClear: () => {
      setInputValueCustomer('')
      setInputValueOrigin('')
    },
  })

  const handleInputChangeCustomer = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueCustomer(value)
  }

  const handleInputChangeOrigin = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValueOrigin(value)
  }

  const filterOptions = createFilterOptions<SelectItem>({
    trim: true,
  })

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
      setInputValueCustomer('')
      setInputValueOrigin('')
    }
  }

  useEffect(() => {
    getCustomerOptions()
  }, [getCustomerOptions])

  useEffect(() => {
    getOriginNumberOptions()
  }, [getOriginNumberOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'origin',
    columnVisibility,
    setValue,
    clearSpecificFields,
    () => {
      setInputValueOrigin('')
      setInputValueCustomer('')
    },
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {!hiddenFields.has('originNumberSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='originNumberSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(originOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, originNumberSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  onInputChange={handleInputChangeOrigin}
                  value={value || []}
                  clearOnBlur={false}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Númer uppruna' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('heiti') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='heiti'
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  label='Heiti uppruna'
                  fullWidth
                  size='small'
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('tengilidirSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='tengilidirSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(customerOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, tengilidirSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  onInputChange={handleInputChangeCustomer}
                  value={value || []}
                  clearOnBlur={false}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Tengiliðir' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('mottakendurSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='mottakendurSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(userOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, mottakendurSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => (
                    <li {...props} key={(option as SelectItem).id}>
                      {getSelectItemOptionLabel(option)}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label='Móttakendur' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('efnislysing') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='efnislysing'
              render={({ field }) => (
                <Tooltip title='Leitaðu af mörgum orðum með því að setja , á milli orðanna.'>
                  <TextField
                    {...field}
                    value={field.value ?? ''}
                    label='Lýsing'
                    fullWidth
                    size='small'
                  />
                </Tooltip>
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('dateFrom') && !hiddenFields.has('dateTo') && (
          <Grid item xs={12} md={4} sx={{ pr: 0.25 }}>
            <DateSearch
              register={register}
              control={control}
              dateFromLabel={'Ár frá'}
              dateToLabel={'Ár til'}
            />
          </Grid>
        )}
      </Grid>
      <FilterControls
        selected={selected}
        clearFilters={clearFilters}
        register={register}
        deselectCheckboxChange={deselectCheckboxChange}
      />
    </form>
  )
}
